import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { PageLayout, PageSection, PageCard, EmptyState } from '../components/layout';
import { 
  Plus, Edit, Trash2, Download, Eye, CheckCircle, XCircle, 
  Loader2, ArrowRight, Search, Calendar, Upload, AlertCircle,
  Info, CheckCircle2, AlertTriangle, User, Mail, Phone, Inbox
} from 'lucide-react';

export default function Styleguide() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [progress, setProgress] = useState(45);

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">FieldReady Style Guide</h1>
          <p className="text-slate-600">UI Components & Design System</p>
        </div>

        {/* Typography Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed']">Typography</CardTitle>
            <CardDescription>Text styles used throughout the application</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Headings */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Headings</h3>
              <div className="space-y-3 bg-white p-4 rounded-lg border">
                <div className="flex items-baseline gap-4">
                  <span className="text-xs text-slate-400 w-20">H1</span>
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900">Page Title</h1>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-xs text-slate-400 w-20">H2</span>
                  <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Section Header</h2>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-xs text-slate-400 w-20">H3</span>
                  <h3 className="text-xl font-semibold text-slate-900">Card Title</h3>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-xs text-slate-400 w-20">H4</span>
                  <h4 className="text-lg font-medium text-slate-900">Subsection</h4>
                </div>
              </div>
            </div>

            {/* Body Text */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Body Text</h3>
              <div className="space-y-3 bg-white p-4 rounded-lg border">
                <div className="flex items-baseline gap-4">
                  <span className="text-xs text-slate-400 w-20">Body</span>
                  <p className="text-base text-slate-700">Regular body text for paragraphs and descriptions.</p>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-xs text-slate-400 w-20">Small</span>
                  <p className="text-sm text-slate-600">Smaller text for captions and secondary info.</p>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-xs text-slate-400 w-20">XS</span>
                  <p className="text-xs text-slate-500">Extra small for labels and timestamps.</p>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-xs text-slate-400 w-20">Muted</span>
                  <p className="text-sm text-slate-400">Muted text for hints and placeholders.</p>
                </div>
              </div>
            </div>

            {/* Special Text */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Special Text</h3>
              <div className="space-y-3 bg-white p-4 rounded-lg border">
                <div className="flex items-baseline gap-4">
                  <span className="text-xs text-slate-400 w-20">Label</span>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">SECTION LABEL</span>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-xs text-slate-400 w-20">Link</span>
                  <a href="#" className="text-amber-600 hover:text-amber-700 font-medium">Clickable Link</a>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-xs text-slate-400 w-20">Mono</span>
                  <span className="font-mono text-sm text-slate-700">MF20260313-001</span>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="text-xs text-slate-400 w-20">Accent</span>
                  <span className="font-medium text-amber-600">Job Number: J20260305-001</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Colors Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed']">Brand Colors</CardTitle>
            <CardDescription>Primary color palette</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Primary - Amber */}
              <div>
                <div className="h-20 bg-amber-500 rounded-t-lg"></div>
                <div className="bg-white p-3 rounded-b-lg border border-t-0">
                  <p className="font-semibold text-sm">Primary</p>
                  <p className="text-xs text-slate-500">amber-500</p>
                </div>
              </div>
              <div>
                <div className="h-20 bg-amber-600 rounded-t-lg"></div>
                <div className="bg-white p-3 rounded-b-lg border border-t-0">
                  <p className="font-semibold text-sm">Primary Dark</p>
                  <p className="text-xs text-slate-500">amber-600</p>
                </div>
              </div>
              {/* Success */}
              <div>
                <div className="h-20 bg-green-600 rounded-t-lg"></div>
                <div className="bg-white p-3 rounded-b-lg border border-t-0">
                  <p className="font-semibold text-sm">Success</p>
                  <p className="text-xs text-slate-500">green-600</p>
                </div>
              </div>
              {/* Danger */}
              <div>
                <div className="h-20 bg-red-600 rounded-t-lg"></div>
                <div className="bg-white p-3 rounded-b-lg border border-t-0">
                  <p className="font-semibold text-sm">Danger</p>
                  <p className="text-xs text-slate-500">red-600</p>
                </div>
              </div>
              {/* Info */}
              <div>
                <div className="h-20 bg-blue-600 rounded-t-lg"></div>
                <div className="bg-white p-3 rounded-b-lg border border-t-0">
                  <p className="font-semibold text-sm">Info</p>
                  <p className="text-xs text-slate-500">blue-600</p>
                </div>
              </div>
              {/* Slate */}
              <div>
                <div className="h-20 bg-slate-800 rounded-t-lg"></div>
                <div className="bg-white p-3 rounded-b-lg border border-t-0">
                  <p className="font-semibold text-sm">Dark</p>
                  <p className="text-xs text-slate-500">slate-800</p>
                </div>
              </div>
              {/* Orange */}
              <div>
                <div className="h-20 bg-orange-600 rounded-t-lg"></div>
                <div className="bg-white p-3 rounded-b-lg border border-t-0">
                  <p className="font-semibold text-sm">PDF/Print</p>
                  <p className="text-xs text-slate-500">orange-600</p>
                </div>
              </div>
              {/* Purple */}
              <div>
                <div className="h-20 bg-purple-600 rounded-t-lg"></div>
                <div className="bg-white p-3 rounded-b-lg border border-t-0">
                  <p className="font-semibold text-sm">Maintenance</p>
                  <p className="text-xs text-slate-500">purple-600</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Buttons Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed']">Buttons</CardTitle>
            <CardDescription>All button variants and states</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Primary Buttons */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Primary Actions</h3>
              <div className="flex flex-wrap gap-3 bg-white p-4 rounded-lg border">
                <Button className="bg-amber-500 hover:bg-amber-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Create New
                </Button>
                <Button className="bg-green-600 hover:bg-green-500">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button className="bg-blue-600 hover:bg-blue-700">
                  Save Changes
                </Button>
                <Button className="bg-orange-600 hover:bg-orange-700">
                  <Download className="w-4 h-4 mr-2" />
                  Print / Save PDF
                </Button>
              </div>
            </div>

            {/* Secondary Buttons */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Secondary Actions</h3>
              <div className="flex flex-wrap gap-3 bg-white p-4 rounded-lg border">
                <Button variant="outline">
                  Cancel
                </Button>
                <Button variant="outline">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button variant="outline">
                  <Eye className="w-4 h-4 mr-2" />
                  View
                </Button>
                <Button variant="outline" className="text-amber-600 border-amber-300 hover:bg-amber-50">
                  <Download className="w-4 h-4 mr-2" />
                  PDF
                </Button>
              </div>
            </div>

            {/* Danger Buttons */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Danger Actions</h3>
              <div className="flex flex-wrap gap-3 bg-white p-4 rounded-lg border">
                <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
                <Button className="bg-red-600 hover:bg-red-700">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Permanently
                </Button>
              </div>
            </div>

            {/* Button Sizes */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Sizes</h3>
              <div className="flex flex-wrap items-center gap-3 bg-white p-4 rounded-lg border">
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600">
                  Small
                </Button>
                <Button className="bg-amber-500 hover:bg-amber-600">
                  Default
                </Button>
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600">
                  Large
                </Button>
              </div>
            </div>

            {/* Button States */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">States</h3>
              <div className="flex flex-wrap items-center gap-3 bg-white p-4 rounded-lg border">
                <Button className="bg-amber-500 hover:bg-amber-600">
                  Normal
                </Button>
                <Button disabled className="bg-amber-500">
                  Disabled
                </Button>
                <Button className="bg-amber-500" disabled>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </Button>
              </div>
            </div>

            {/* Icon-only Buttons */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Icon Only</h3>
              <div className="flex flex-wrap items-center gap-3 bg-white p-4 rounded-lg border">
                <Button variant="outline" size="sm" className="w-8 h-8 p-0">
                  <Plus className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" className="w-8 h-8 p-0">
                  <Edit className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" className="w-8 h-8 p-0 text-red-600 border-red-300 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm">
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form Fields Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed']">Form Fields</CardTitle>
            <CardDescription>Input components and form controls</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Text Inputs */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Text Inputs</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white p-4 rounded-lg border">
                <div className="space-y-2">
                  <Label htmlFor="default">Default Input</Label>
                  <Input id="default" placeholder="Enter text..." />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="filled">Filled Input</Label>
                  <Input id="filled" defaultValue="Pre-filled value" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="disabled">Disabled Input</Label>
                  <Input id="disabled" placeholder="Disabled" disabled />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="required">Required Input *</Label>
                  <Input id="required" placeholder="Required field" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="search">Search Input</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="search" placeholder="Search..." className="pl-10" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date">Date Input</Label>
                  <Input id="date" type="date" />
                </div>
              </div>
            </div>

            {/* Select */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Select Dropdown</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white p-4 rounded-lg border">
                <div className="space-y-2">
                  <Label>Default Select</Label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Select option..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="option1">Option 1</SelectItem>
                      <SelectItem value="option2">Option 2</SelectItem>
                      <SelectItem value="option3">Option 3</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status Select</Label>
                  <Select defaultValue="pending">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Textarea */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Textarea</h3>
              <div className="bg-white p-4 rounded-lg border">
                <div className="space-y-2">
                  <Label htmlFor="textarea">Notes / Description</Label>
                  <Textarea 
                    id="textarea" 
                    placeholder="Enter notes or description..."
                    className="min-h-[100px]"
                  />
                </div>
              </div>
            </div>

            {/* Checkboxes & Switches */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Checkboxes & Switches</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white p-4 rounded-lg border">
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox id="check1" />
                    <Label htmlFor="check1">Checkbox unchecked</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="check2" defaultChecked />
                    <Label htmlFor="check2">Checkbox checked</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="check3" disabled />
                    <Label htmlFor="check3" className="text-slate-400">Checkbox disabled</Label>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Switch off</Label>
                    <Switch />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Switch on</Label>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-400">Switch disabled</Label>
                    <Switch disabled />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Badges Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed']">Badges & Status</CardTitle>
            <CardDescription>Status indicators and labels</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Status Badges */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Status Badges</h3>
              <div className="flex flex-wrap gap-3 bg-white p-4 rounded-lg border">
                <Badge className="bg-slate-100 text-slate-700">Draft</Badge>
                <Badge className="bg-blue-100 text-blue-700">Pending Review</Badge>
                <Badge className="bg-amber-100 text-amber-700">In Progress</Badge>
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Approved
                </Badge>
                <Badge className="bg-red-100 text-red-700">Rejected</Badge>
                <Badge className="bg-purple-100 text-purple-800">Maintenance</Badge>
              </div>
            </div>

            {/* Priority Badges */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Priority Badges</h3>
              <div className="flex flex-wrap gap-3 bg-white p-4 rounded-lg border">
                <Badge className="bg-slate-100 text-slate-700">Low</Badge>
                <Badge className="bg-blue-100 text-blue-700">Normal</Badge>
                <Badge className="bg-amber-100 text-amber-700">High</Badge>
                <Badge className="bg-red-100 text-red-700">Urgent</Badge>
              </div>
            </div>

            {/* Type Badges */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Form Type Badges</h3>
              <div className="flex flex-wrap gap-3 bg-white p-4 rounded-lg border">
                <Badge className="bg-blue-100 text-blue-800">PreCon</Badge>
                <Badge className="bg-green-100 text-green-800">Soft Dig</Badge>
                <Badge className="bg-purple-100 text-purple-800">Locate</Badge>
                <Badge className="bg-amber-100 text-amber-800">Check Out</Badge>
                <Badge className="bg-slate-100 text-slate-800">Check In</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cards Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed']">Cards</CardTitle>
            <CardDescription>Card layout examples</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Basic Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-['Barlow_Condensed']">Basic Card</CardTitle>
                  <CardDescription>Card with header and content</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600">Card content goes here. This is a standard card layout used for displaying grouped information.</p>
                </CardContent>
              </Card>

              {/* Interactive Card */}
              <Card className="hover:border-amber-300 cursor-pointer transition-colors">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-['Barlow_Condensed']">Interactive Card</CardTitle>
                    <Badge className="bg-green-100 text-green-700">Active</Badge>
                  </div>
                  <CardDescription>J20260305-001</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm">
                      <Eye className="w-3 h-3 mr-1" />
                      View
                    </Button>
                    <Button variant="outline" size="sm">
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>

        {/* Dialogs Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed']">Dialogs & Modals</CardTitle>
            <CardDescription>Modal dialog patterns</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Dialog Examples</h3>
              <div className="flex flex-wrap gap-3 bg-white p-4 rounded-lg border">
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-amber-500 hover:bg-amber-600">
                      <Plus className="w-4 h-4 mr-2" />
                      Open Dialog
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Item</DialogTitle>
                      <DialogDescription>
                        Fill in the details below to create a new item.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Name</Label>
                        <Input id="name" placeholder="Enter name..." />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea id="description" placeholder="Enter description..." />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button className="bg-amber-500 hover:bg-amber-600" onClick={() => setDialogOpen(false)}>
                        Create
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed']">Tabs</CardTitle>
            <CardDescription>Tab navigation patterns</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="mt-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-slate-600">Overview tab content. Use tabs to organize related content into sections.</p>
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="details" className="mt-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-slate-600">Details tab content with more specific information.</p>
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="settings" className="mt-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-slate-600">Settings tab content for configuration options.</p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Alerts Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed']">Alerts & Notifications</CardTitle>
            <CardDescription>Alert message patterns</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>Information</AlertTitle>
              <AlertDescription>
                This is an informational alert for general notices.
              </AlertDescription>
            </Alert>
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-800">Success</AlertTitle>
              <AlertDescription className="text-green-700">
                Your changes have been saved successfully.
              </AlertDescription>
            </Alert>
            <Alert className="border-amber-200 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800">Warning</AlertTitle>
              <AlertDescription className="text-amber-700">
                Please review the information before proceeding.
              </AlertDescription>
            </Alert>
            <Alert className="border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertTitle className="text-red-800">Error</AlertTitle>
              <AlertDescription className="text-red-700">
                Something went wrong. Please try again.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Tables Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed']">Tables</CardTitle>
            <CardDescription>Data table patterns</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Job #</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">J20260305-001</TableCell>
                    <TableCell>Acme Corporation</TableCell>
                    <TableCell><Badge className="bg-green-100 text-green-700">Complete</Badge></TableCell>
                    <TableCell>Mar 5, 2026</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm"><Eye className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm"><Edit className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">J20260306-002</TableCell>
                    <TableCell>Tech Industries</TableCell>
                    <TableCell><Badge className="bg-amber-100 text-amber-700">In Progress</Badge></TableCell>
                    <TableCell>Mar 6, 2026</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm"><Eye className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm"><Edit className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">J20260307-003</TableCell>
                    <TableCell>Global Services</TableCell>
                    <TableCell><Badge className="bg-blue-100 text-blue-700">Pending</Badge></TableCell>
                    <TableCell>Mar 7, 2026</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm"><Eye className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm"><Edit className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Avatars Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed']">Avatars & User Display</CardTitle>
            <CardDescription>User representation patterns</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Avatar Sizes</h3>
              <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-lg border">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-amber-500 text-slate-900 text-xs">SM</AvatarFallback>
                </Avatar>
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-blue-500 text-white text-sm">MD</AvatarFallback>
                </Avatar>
                <Avatar className="h-12 w-12">
                  <AvatarFallback className="bg-green-500 text-white">LG</AvatarFallback>
                </Avatar>
                <Avatar className="h-16 w-16">
                  <AvatarFallback className="bg-purple-500 text-white text-lg">XL</AvatarFallback>
                </Avatar>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">User Card Pattern</h3>
              <div className="bg-white p-4 rounded-lg border max-w-sm">
                <div className="flex items-center gap-3">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback className="bg-cyan-500 text-white">CH</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-slate-900">Chan Hoyle</p>
                    <p className="text-sm text-slate-500">Super User</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Loading States Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed']">Loading States</CardTitle>
            <CardDescription>Progress and skeleton patterns</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Progress Bar</h3>
              <div className="space-y-4 bg-white p-4 rounded-lg border">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Upload Progress</span>
                    <span className="text-slate-900 font-medium">{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setProgress(Math.max(0, progress - 10))}>-10%</Button>
                  <Button size="sm" variant="outline" onClick={() => setProgress(Math.min(100, progress + 10))}>+10%</Button>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Skeleton Loaders</h3>
              <div className="space-y-4 bg-white p-4 rounded-lg border">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[200px]" />
                    <Skeleton className="h-4 w-[150px]" />
                  </div>
                </div>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-20 w-full" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tooltips Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed']">Tooltips</CardTitle>
            <CardDescription>Hover information patterns</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 bg-white p-4 rounded-lg border">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline">Hover me</Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>This is a tooltip</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon">
                      <Info className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Click for more information</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </CardContent>
        </Card>

        {/* Page Layout Pattern */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed']">Page Layout Pattern</CardTitle>
            <CardDescription>Standard page structure using the PageLayout component</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Live Example using PageLayout */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Live Example</h3>
              <div className="bg-slate-100 rounded-lg p-4 border">
                <PageLayout
                  title="Jobs"
                  description="Manage all your jobs in one place"
                  badge={<Badge className="bg-amber-100 text-amber-700">24 Active</Badge>}
                  actions={
                    <>
                      <Button variant="outline" size="sm">
                        <Search className="w-4 h-4 mr-2" />
                        Filter
                      </Button>
                      <Button className="bg-amber-500 hover:bg-amber-600" size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        New Job
                      </Button>
                    </>
                  }
                >
                  <PageCard>
                    <p className="text-sm text-slate-500 text-center py-8">
                      Main content area - Cards, Tables, Forms, etc.
                    </p>
                  </PageCard>
                </PageLayout>
              </div>
            </div>

            {/* Empty State Example */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Empty State</h3>
              <div className="bg-white rounded-lg border">
                <EmptyState
                  icon={Inbox}
                  title="No jobs found"
                  description="Get started by creating your first job"
                  action={
                    <Button className="bg-amber-500 hover:bg-amber-600">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Job
                    </Button>
                  }
                />
              </div>
            </div>

            {/* Usage Code */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-semibold text-sm mb-2">Usage with PageLayout Component:</h4>
              <pre className="text-xs bg-slate-900 text-slate-100 p-3 rounded overflow-x-auto">
{`import { PageLayout, PageCard, EmptyState } from '../components/layout';

// Basic usage
<PageLayout
  title="Page Title"
  description="Page description"
  actions={<Button>Add New</Button>}
  backLink="/previous"
  backLabel="Back to List"
>
  <PageCard>
    {/* Your content here */}
  </PageCard>
</PageLayout>

// Empty state
<EmptyState
  icon={Inbox}
  title="No items found"
  description="Get started by creating your first item"
  action={<Button>Create Item</Button>}
/>`}
              </pre>
            </div>
          </CardContent>
        </Card>

        {/* Spacing Reference */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed']">Spacing Reference</CardTitle>
            <CardDescription>Common spacing values used in layouts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 bg-white p-4 rounded-lg border">
              <div className="flex items-center gap-4">
                <span className="text-xs text-slate-400 w-20">gap-1</span>
                <div className="flex gap-1">
                  <div className="w-8 h-8 bg-amber-200 rounded"></div>
                  <div className="w-8 h-8 bg-amber-200 rounded"></div>
                  <div className="w-8 h-8 bg-amber-200 rounded"></div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-xs text-slate-400 w-20">gap-2</span>
                <div className="flex gap-2">
                  <div className="w-8 h-8 bg-amber-300 rounded"></div>
                  <div className="w-8 h-8 bg-amber-300 rounded"></div>
                  <div className="w-8 h-8 bg-amber-300 rounded"></div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-xs text-slate-400 w-20">gap-4</span>
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-amber-400 rounded"></div>
                  <div className="w-8 h-8 bg-amber-400 rounded"></div>
                  <div className="w-8 h-8 bg-amber-400 rounded"></div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-xs text-slate-400 w-20">gap-6</span>
                <div className="flex gap-6">
                  <div className="w-8 h-8 bg-amber-500 rounded"></div>
                  <div className="w-8 h-8 bg-amber-500 rounded"></div>
                  <div className="w-8 h-8 bg-amber-500 rounded"></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-slate-500 py-4">
          <p>FieldReady Style Guide • Last Updated: {new Date().toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  );
}
